import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from 'lucide-react';

const ToastContext = createContext(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside a <ToastProvider>');
  return ctx;
};

let nextId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const add = useCallback((variant, message, opts = {}) => {
    const id = ++nextId;
    setToasts((prev) => [...prev, { id, variant, message }]);
    const duration = opts.duration ?? 4000;
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
    return id;
  }, []);

  const toast = useMemo(() => ({
    success: (msg, opts) => add('success', msg, opts),
    error:   (msg, opts) => add('error',   msg, opts),
    info:    (msg, opts) => add('info',    msg, opts),
    warning: (msg, opts) => add('warning', msg, opts),
  }), [add]);

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
};

const variantStyles = {
  success: { bg: 'bg-success-50 border-success-200',  text: 'text-success-700',  Icon: CheckCircle2 },
  error:   { bg: 'bg-danger-50 border-danger-200',    text: 'text-danger-700',   Icon: XCircle },
  info:    { bg: 'bg-primary-50 border-primary-200',  text: 'text-primary-700',  Icon: Info },
  warning: { bg: 'bg-warning-50 border-warning-200',  text: 'text-warning-700',  Icon: AlertTriangle },
};

const ToastContainer = ({ toasts, dismiss }) => (
  <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
    {toasts.map((t) => {
      const v = variantStyles[t.variant] || variantStyles.info;
      const { Icon } = v;
      return (
        <div
          key={t.id}
          role="alert"
          className={`pointer-events-auto flex items-start gap-3 px-4 py-3 border ${v.bg} ${v.text} rounded-lg shadow-card-hover min-w-[280px] max-w-md animate-in fade-in slide-in-from-right-2`}
        >
          <Icon size={20} className="flex-shrink-0 mt-0.5" />
          <p className="flex-1 text-sm">{t.message}</p>
          <button
            onClick={() => dismiss(t.id)}
            className="text-current opacity-60 hover:opacity-100 transition-opacity"
            aria-label="Dismiss notification"
          >
            <X size={16} />
          </button>
        </div>
      );
    })}
  </div>
);
